import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import useTeamsResources from '../shared/resources/api/v1/admin/useTeamsResources'

const TeamContext = createContext(null)

export function TeamProvider({ children }) {
  const { teamSlug } = useParams()
  const { get } = useTeamsResources()
  const [team, setTeam] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const loadedIdRef = useRef(null)
  const teamRef = useRef(team)
  teamRef.current = team

  const loadTeam = useCallback(() => {
    if (!teamSlug) return Promise.resolve(null)
    if (loadedIdRef.current === teamSlug && teamRef.current) {
      return Promise.resolve(teamRef.current)
    }

    setLoading(true)
    setError(null)
    return get(teamSlug)
      .then((response) => {
        setTeam(response.team)
        loadedIdRef.current = teamSlug
        return response.team
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
      })
  }, [teamSlug, get])

  useEffect(() => {
    loadTeam()
  }, [teamSlug])

  const value = useMemo(() => ({ team, loading, error, reload: loadTeam }), [team, loading, error, loadTeam])

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  )
}

export function useTeam() {
  const context = useContext(TeamContext)
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider')
  }
  return context
}
